import * as React from 'react';

import { cn } from '@/lib/utils';

const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(function Card(
  { className, ...properties },
  reference
) {
  return (
    <div
      ref={reference}
      className={cn(
        // Game panel styling
        'relative rounded-lg',
        // Gradient background with depth
        'bg-gradient-to-b from-card/95 to-card',
        // Luminescent border
        'border border-border/60',
        // Shadow with inner glow
        'shadow-[0_4px_20px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.03),inset_0_0_30px_rgba(34,211,238,0.02)]',
        // Corner accents (using pseudo-element alternative via border)
        'before:absolute before:inset-0 before:rounded-lg before:pointer-events-none',
        'before:bg-gradient-to-b before:from-white/[0.03] before:to-transparent',
        className
      )}
      {...properties}
    />
  );
});

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...properties }, reference) {
    return (
      <div
        ref={reference}
        className={cn(
          'flex flex-col space-y-1.5 p-6',
          // Subtle bottom border for separation
          'border-b border-border/30',
          className
        )}
        {...properties}
      />
    );
  }
);

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  function CardTitle({ className, ...properties }, reference) {
    return (
      <h3
        ref={reference}
        className={cn(
          'font-display font-semibold leading-none tracking-wide',
          // Subtle text glow
          'text-foreground',
          className
        )}
        {...properties}
      />
    );
  }
);

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(function CardDescription({ className, ...properties }, reference) {
  return (
    <p ref={reference} className={cn('text-sm text-muted-foreground', className)} {...properties} />
  );
});

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardContent({ className, ...properties }, reference) {
    return <div ref={reference} className={cn('p-6 pt-4', className)} {...properties} />;
  }
);

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...properties }, reference) {
    return (
      <div
        ref={reference}
        className={cn(
          'flex items-center p-6 pt-0',
          // Top border for separation
          'border-t border-border/20 mt-2 pt-4',
          className
        )}
        {...properties}
      />
    );
  }
);

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
